import { useState } from "react";
import { toast } from "sonner";
import { Copy, Check } from "lucide-react";

/**
 * CopyableEmail — displays email as plain text; copies to clipboard on click.
 * NEVER uses mailto:. Hospitality buyers prefer to capture the email into their CRM.
 *
 * Variants:
 *   default — small inline text
 *   button  — pill button (used in CTA blocks)
 */
export default function CopyableEmail({
  email = "archana@auraadesigns.com",
  variant = "default",
  className = "",
  testid = "copy-email",
}) {
  const [copied, setCopied] = useState(false);

  const onCopy = async (e) => {
    e.preventDefault();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(email);
      } else {
        const ta = document.createElement("textarea");
        ta.value = email;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success("Email address copied");
      window.setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Couldn't copy. Email: " + email);
    }
  };

  if (variant === "button") {
    return (
      <button
        type="button"
        onClick={onCopy}
        data-testid={testid}
        aria-label={`Copy email ${email}`}
        className={`group inline-flex items-center justify-center gap-3 border border-bone/40 text-bone px-9 py-4 text-sm tracking-[0.18em] uppercase font-medium transition-all duration-500 hover:border-bone hover:bg-bone/5 ${className}`}
      >
        <span>{email}</span>
        {copied ? <Check size={14} /> : <Copy size={13} className="opacity-60 group-hover:opacity-100 transition-opacity" />}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onCopy}
      data-testid={testid}
      aria-label={`Copy email ${email}`}
      className={`inline-flex items-center gap-2 text-espresso hover:text-oxblood transition-colors duration-300 break-all text-left ${className}`}
    >
      <span>{email}</span>
      {copied ? (
        <Check size={13} className="text-oxblood shrink-0" />
      ) : (
        <Copy size={12} className="opacity-50 group-hover:opacity-100 shrink-0" />
      )}
    </button>
  );
}
